import FunctionHelper from "../helper/FunctionHelper";

/**
 * A Timer is used to wrap/replace the {@link window.setTimeout} /
 * {@link window.setInterval} functions and makes them reusable and pauseable
 */
export default class Timer {
	private timeout: number;
	private startTime: Date;
	private running: boolean;
	private remaining: number;
	private callback: () => void;
	private time: number;
	private loop: boolean;

	/**
	 * Constructs a {@link Timer} instance
	 */
	constructor(callback: () => void, timeout: number, loop?: boolean) {
		this.callback = callback;
		this.time = timeout;
		this.remaining = timeout;
		this.running = false;
		this.startTime = new Date();
		this.timeout = 0;
		this.loop = FunctionHelper.applyValue(loop, false);
	}

	/**
	 * Starts / Continues the timer
	 */
	public start(): void {
		this.running = true;
		this.startTime = new Date();
		window.clearTimeout(this.timeout);
		this.timeout = window.setTimeout(() => {
			this.callback();

			if (this.loop) {
				this.reset();
			}
		}, this.remaining);
	}

	/**
	 * Pauses the timer (can be continued with {@link Timer.start})
	 */
	public pause(): void {
		this.running = false;
		window.clearTimeout(this.timeout);
		this.remaining -= new Date().getTime() - this.startTime.getTime();
	}

	/**
	 * Resets the timer and starts it again. Can be called with timeout to set the time to a different value **once**.
	 * @param {number} timeout
	 */
	public reset(timeout?: number): void {
		this.remaining = FunctionHelper.applyValue(timeout, this.time);
		this.start();
	}

	/**
	 * Returns the remaining time
	 */
	public getRemaining(): number {
		if (this.running) {
			this.pause();
			this.start();
		}

		return this.remaining;
	}

	/**
	 * Returns the currently set time value
	 */
	public getTime(): number {
		return this.time
	}

	/**
	 * Sets the countdown time for the timer
	 */
	public setTime(value: number): void {
		this.time = value;
	}

	/**
	 * Sets the callback function for the timer
	 */
	public setCallback(value: () => void): void {
		this.callback = value;
	}

	/**
	 * Checks if the timer is running
	 */
	public isRunning(): boolean {
		return this.running;
	}

	/**
	 * Sets if the timer is looping
	 */
	public setLooping(value: boolean): void {
		this.loop = value;
	}

	/**
	 * Checks if the timer is looping
	 */
	public isLooping(): boolean {
		return this.loop;
	}
}